import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { authenticatedFetch } from '../../../utils/apiUtils';
import { createLogger } from '../../../utils/debugUtils';

const logger = createLogger('Step2_SelectColumn');

export default function Step2_SelectColumn({
  wizardState,
  onUpdateState,
  onNext,
  onBack,
  loading,
  setLoading
}) {
  const [search, setSearch] = useState('');
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [error, setError] = useState(null);
  const [columnPreview, setColumnPreview] = useState([]);
  const [previewStats, setPreviewStats] = useState(null);

  // Filter columns based on search
  useEffect(() => {
    if (!wizardState.columns || wizardState.columns.length === 0) {
      setFilteredColumns([]);
      return;
    }

    const filtered = wizardState.columns.filter(col =>
      col.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredColumns(filtered);
  }, [search, wizardState.columns]);

  // Get preview data for selected column
  const getColumnPreview = async (columnName) => {
    if (!columnName || !wizardState.fileId) return;

    try {
      setLoading(true);
      logger.info('Fetching column preview', {
        fileId: wizardState.fileId,
        filePath: wizardState.filePath,
        column: columnName
      });

      // Build URL with optional filePath parameter for cache miss fallback
      const filePathParam = wizardState.filePath ? `&filePath=${encodeURIComponent(wizardState.filePath)}` : '';
      const url = `/api/research/preview/${wizardState.fileId}/${encodeURIComponent(columnName)}?limit=10${filePathParam}`;

      // Use authenticatedFetch instead of raw fetch
      const response = await authenticatedFetch(url);

      if (response.ok) {
        const data = await response.json();
        // Filter to only show valid entries (hide social media, free emails, invalid)
        const validPreview = (data.preview || []).filter(item => item.type === 'url' || item.type === 'email');
        setColumnPreview(validPreview);
        setPreviewStats(data.stats || null);
        logger.info('Column preview loaded', {
          total: data.preview?.length || 0,
          valid: validPreview.length,
          stats: data.stats
        });
      } else {
        // Preview not available - not critical, just log
        logger.warn('Column preview not available', { status: response.status });
        setColumnPreview([]);
        setPreviewStats(null);
      }
    } catch (error) {
      // Preview failure is not critical - don't show error to user
      logger.error('Error fetching column preview:', error);
      setColumnPreview([]);
      setPreviewStats(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle column selection
  const handleSelectColumn = (columnName) => {
    onUpdateState({ selectedColumn: columnName });
    getColumnPreview(columnName);
  };

  // Handle next button
  const handleNext = () => {
    if (!wizardState.selectedColumn) {
      setError('Please select a column containing website URLs.');
      return;
    }

    setError(null);
    onNext();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Select the column containing website URLs that you want to analyze.
      </Typography>

      {/* 50/50 Split Layout - Match List Cleaner */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Panel: Column List */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{
            p: 3,
            backgroundColor: '#161c3e',
            borderRadius: 2,
            height: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" gutterBottom>
              Available Columns ({filteredColumns.length})
            </Typography>

            {/* Search */}
            <TextField
              placeholder="Search columns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            {/* Column List */}
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 1,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px'
              }
            }}>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={wizardState.selectedColumn || ''}
                  onChange={(e) => handleSelectColumn(e.target.value)}
                >
                  {filteredColumns.map((column) => (
                    <FormControlLabel
                      key={column}
                      value={column}
                      control={<Radio />}
                      label={column}
                      sx={{
                        mx: 1,
                        my: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        },
                        ...(wizardState.selectedColumn === column && {
                          backgroundColor: 'rgba(0, 149, 255, 0.1)',
                          border: '1px solid rgba(0, 149, 255, 0.3)'
                        })
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {filteredColumns.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'No columns match your search' : 'No columns available'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Right Panel: Preview */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{
            p: 3,
            backgroundColor: '#161c3e',
            borderRadius: 2,
            height: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" gutterBottom>
              Column Preview
            </Typography>

            {wizardState.selectedColumn ? (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <Card sx={{
                  mb: 2,
                  backgroundColor: '#0D0D21',
                  border: '1px solid rgba(0, 149, 255, 0.3)'
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Column
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {wizardState.selectedColumn}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {wizardState.rowCount} total rows
                    </Typography>
                  </CardContent>
                </Card>

                {/* Statistics Summary - Warning if issues found */}
                {previewStats && (previewStats.socialMedia > 0 || previewStats.freeEmails > 0 || (previewStats.invalid - previewStats.socialMedia - previewStats.freeEmails) > 0) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      ⚠️ Validation Issues Detected (First 10 rows)
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {previewStats.socialMedia > 0 && `• ${previewStats.socialMedia} social media URLs will be filtered out (LinkedIn, Facebook, etc.)\n`}
                      {previewStats.freeEmails > 0 && `• ${previewStats.freeEmails} free email addresses will be filtered out (Gmail, Yahoo, etc.)\n`}
                      {(previewStats.invalid - previewStats.socialMedia - previewStats.freeEmails) > 0 &&
                        `• ${previewStats.invalid - previewStats.socialMedia - previewStats.freeEmails} invalid entries will be skipped\n`}
                      <strong>Only {previewStats.valid} of 10 sample rows are valid.</strong> These will be filtered during processing.
                    </Typography>
                  </Alert>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Sample Values (First 10 rows)
                </Typography>

                {columnPreview.length > 0 ? (
                  <List sx={{ pt: 0 }}>
                    {columnPreview.map((item, index) => {
                      const isEmail = item.type === 'email';
                      return (
                        <ListItem
                          key={index}
                          sx={{
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: 'rgba(0, 149, 255, 0.05)'
                          }}
                        >
                          <ListItemText
                            primary={item.value || '(empty)'}
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                                <Typography variant="caption" color="success.main">
                                  {isEmail ? `Business email → ${item.domain}` : 'Valid company website'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      {previewStats && previewStats.valid === 0
                        ? 'No valid entries found in preview. Column may not contain usable data.'
                        : 'Preview not available. Select a column to continue.'
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed rgba(255, 255, 255, 0.1)',
                borderRadius: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  Select a column from the list to see a preview
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
